import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Download, FileText, Book, FileCheck } from 'lucide-react';
import Layout from '../../components/layout/layout';
import Button from '../../components/ui/button';
import { supabase } from '../../lib/supabase';
import { WebPageSchema, BreadcrumbSchema } from '../../lib/utils/schema-utils';

interface Resource {
  id: string;
  title: string;
  description: string;
  file_url: string;
  category: 'guide' | 'template' | 'whitepaper' | 'toolkit';
  downloads: number;
}

export default function RessourcesPage() {
  const [resources, setResources] = useState<Resource[]>([]);
  const [selectedResource, setSelectedResource] = useState<Resource | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    company: '',
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchResources = async () => {
      const { data, error } = await supabase
        .from('resources')
        .select('*')
        .order('downloads', { ascending: false });

      if (error) {
        console.error('Error fetching resources:', error);
        return;
      }

      setResources(data || []);
      setIsLoading(false);
    };

    fetchResources();
  }, []);

  const handleDownload = (resource: Resource) => {
    setSelectedResource(resource);
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedResource) return;

    try {
      // Save lead information
      await supabase.from('leads').insert([
        {
          email: formData.email,
          first_name: formData.firstName,
          last_name: formData.lastName,
          company: formData.company,
          resource_id: selectedResource.id
        }
      ]);

      // Update download count
      await supabase
        .from('resources')
        .update({ downloads: selectedResource.downloads + 1 })
        .eq('id', selectedResource.id);

      // Trigger download
      window.location.href = selectedResource.file_url;
      
      // Reset form
      setShowForm(false);
      setSelectedResource(null);
      setFormData({
        email: '',
        firstName: '',
        lastName: '',
        company: '',
      });
    } catch (error) {
      console.error('Error saving lead:', error);
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'guide':
        return <Book className="w-6 h-6" />;
      case 'template':
        return <FileCheck className="w-6 h-6" />;
      case 'whitepaper':
        return <FileText className="w-6 h-6" />;
      default:
        return <FileText className="w-6 h-6" />;
    }
  };

  return (
    <Layout>
      <WebPageSchema
        page={{
          title: "Ressources RH gratuites | recruteurs.io",
          description: "Guides pratiques, modèles de documents, livres blancs... Tout ce qu'il vous faut pour recruter et manager efficacement.",
          url: "https://recruteurs.io/ressources/ressources"
        }}
      />
      
      <BreadcrumbSchema
        items={[
          { name: "Accueil", url: "https://recruteurs.io" },
          { name: "Ressources", url: "https://recruteurs.io/ressources" },
          { name: "Ressources gratuites", url: "https://recruteurs.io/ressources/ressources" }
        ]}
      />

      <div className="py-20">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Ressources RH gratuites
            </h1>
            <p className="text-xl text-neutral-600">
              Guides pratiques, modèles de documents, livres blancs... Tout ce qu'il vous faut pour recruter et manager efficacement.
            </p>
          </div>

          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="bg-white rounded-xl p-6 shadow-lg">
                    <div className="h-6 bg-neutral-200 rounded w-3/4 mb-4" />
                    <div className="h-4 bg-neutral-200 rounded w-full mb-2" />
                    <div className="h-4 bg-neutral-200 rounded w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {resources.map((resource) => (
                <motion.div
                  key={resource.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="text-primary">
                      {getCategoryIcon(resource.category)}
                    </div>
                    <div className="text-sm text-neutral-500">
                      {resource.downloads} téléchargements
                    </div>
                  </div>
                  <h3 className="text-xl font-bold mb-2">{resource.title}</h3>
                  <p className="text-neutral-600 mb-6">{resource.description}</p>
                  <Button
                    variant="outline"
                    onClick={() => handleDownload(resource)}
                    className="w-full"
                  >
                    <Download size={18} className="mr-2" />
                    Télécharger
                  </Button>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Download Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-8 max-w-md w-full">
            <h2 className="text-2xl font-bold mb-6">
              Télécharger {selectedResource?.title}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Email professionnel</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Prénom</label>
                <input
                  type="text"
                  required
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Nom</label>
                <input
                  type="text"
                  required
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Entreprise</label>
                <input
                  type="text"
                  required
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                />
              </div>
              <div className="flex gap-4 mt-6">
                <Button
                  variant="gradient"
                  type="submit"
                  className="flex-1"
                >
                  Télécharger
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowForm(false)}
                  className="flex-1"
                >
                  Annuler
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}